import { useContext } from 'react'
import {
  ActionDependant,
  GlobalDataContext,
  TemplateContext,
  evaluateTemplateValue,
  useEvaluatedAttributes,
} from '@ea-lab/reactive-json'
import { cropUrl, cropSrcSet, isRemoteUrl } from '../../../utils/cropUrl'
import { BuilderContext } from '../BuilderContextInstance'
import {
  CENTERED_FOCUS,
  SIZING_BANDS,
  boxWidths,
  coverWidth,
  focusOf,
  focusPosition,
  ratiosDiffer,
  resolvedBoxes,
  sizingOf,
  sizingSizes,
  sizingVars,
  supportsFocus,
  unionWidths,
} from '../editable/imageSizing'
import { quantize } from '../../../utils/cropUrl'

/**
 * ReactiveJSON primitive — the canonical image primitive.
 *
 * Native-first by design : a plain <img> with `loading="lazy"` (browser-managed
 * deferral, no JS) + `decoding="async"` + a responsive `srcset`, and a CSS
 * `aspect-ratio` so the box is reserved (no layout shift). Keeping the `src` in
 * the markup lets the browser's preload scanner do its job — strictly better
 * than withholding it behind an IntersectionObserver.
 *
 * Accepts every source shape:
 *   - dynamic descriptor `{src, scheme, cropBase, width, height, alt, title}`
 *     (the decoupled_dynamic_image formatter) → cropped on the fly at the
 *     requested box, with a multi-DPR srcset.
 *   - drupal image `{url, alt, width, height}` (image-drupal editor).
 *   - a bare URL string.
 *
 * YAML usage:
 *   - type: LazyImage
 *     value: ~~.cover            # descriptor | {url} | "https://…"
 *     op: sc                     # s | c | sc | f   (default sc, dynamic only)
 *     width: 800
 *     height: 600
 *     widths: [320, 480, 640, 800]   # responsive ladder → srcset (keeps ratio)
 *     sizes: "(max-width: 768px) 100vw, 800px"
 *     loading: lazy              # lazy | eager     (default lazy)
 *     token: ~~.userInfo.imageCropToken   # only if token_mode: session
 *     attributes: { class: "rounded-xl w-full object-cover" }
 *
 * ---------------------------------------------------------------------------
 * DIMENSIONNEMENT PAR POINT DE RUPTURE (`value.sizing`)
 *
 * Quand la valeur porte un `sizing` (écrit par la poignée de redimensionnement
 * du canevas et par l'éditeur `image-sizing` — voir `editable/imageSizing.js`),
 * c'est LUI qui commande, et les props YAML `width`/`height`/`widths`/`sizes`/
 * `op` passent au second plan : l'éditeur a réglé la boîte à l'écran, elle prime
 * sur la valeur écrite d'avance dans la déf du bloc.
 *
 * Trois rendus, dans cet ordre :
 *  1. EN ÉDITION — une seule source, celle de l'appareil prévisualisé. Le
 *     canevas bascule d'appareil en changeant la largeur d'un CONTENEUR ; or un
 *     `<source media>` interroge la FENÊTRE, qui elle ne bouge pas. Sans ce
 *     court-circuit, la poignée redimensionnerait la boîte sans jamais changer
 *     l'image servie.
 *  2. PUBLIÉ, rapports de forme identiques — un `<img>` unique : une seule
 *     forme, plusieurs largeurs, `sizes` par bande. C'est le cas courant, et il
 *     ne coûte pas un nœud de plus.
 *  1 bis. EN ÉDITION, CADRAGE DÉPLAÇABLE — l'image ENTIÈRE à l'échelle de
 *     couverture, cadrée par `object-position`. Le point d'intérêt
 *     (`sizing.focus`) se règle en faisant glisser l'image dans son cadre : si
 *     le canevas servait la dérivée déjà découpée, elle remplirait sa boîte au
 *     pixel près et le glissé ne montrerait RIEN jusqu'au relâchement. Publié,
 *     c'est le serveur qui découpe au point voulu (`p_{x}_{y}` dans le nom de
 *     la dérivée) : mêmes pixels visibles, sans le débord téléchargé.
 *  3. PUBLIÉ, rapports différents — un `<picture>` : c'est le seul balisage qui
 *     sert une DÉCOUPE différente par bande. Limite assumée : ses `media`
 *     interrogent la fenêtre alors que la boîte CSS suit le conteneur, donc un
 *     bloc posé dans une colonne étroite reçoit la découpe desktop dans une
 *     boîte mobile — `object-fit` recadre, l'image n'est jamais déformée.
 */

/** Marqueur des recettes `.builder-img-sized` d'index.css (boîte par bande). */
const SIZED_CLASS = 'builder-img-sized'

/** Marqueur de la recette `object-position` (le point d'intérêt). */
const POS_CLASS = 'builder-img-pos'

/**
 * Marqueur de la boîte d'APERÇU : en édition, une image déplaçable qui n'a pas
 * de boîte réglée est servie ENTIÈRE, donc avec un autre rapport de forme que
 * la dérivée découpée. Sans cette recette, le bloc changerait de mise en page
 * en passant en édition (une découpe 1200×560 rendue en 1200×1200).
 */
const PREVIEW_FRAME_CLASS = 'builder-img-preview-frame'

/** Normalize the accepted shapes into a single internal descriptor. */
function resolveSource(value) {
  if (!value) return null
  if (typeof value === 'string') {
    if (!value) return null
    return isRemoteUrl(value) ? { kind: 'remote', url: value } : { kind: 'url', url: value }
  }
  if (value.src && (value.cropBase || value.scheme)) {
    return { kind: 'dynamic', ...value }
  }
  if (value.url) {
    // A bare remote URL is materialized + cropped server-side; a local one is
    // rendered as-is.
    const kind = isRemoteUrl(value.url) ? 'remote' : 'url'
    return { kind, url: value.url, alt: value.alt, width: value.width, height: value.height }
  }
  return null
}

export const LazyImage = ({ props }) => {
  const globalDataContext = useContext(GlobalDataContext)
  const templateContext = useContext(TemplateContext)
  // Le contexte du builder est ABSENT hors du builder (une racine
  // reactive-json montée ailleurs) : `useContext` rend alors `null`, jamais une
  // erreur — c'est pourquoi ce primitif ne passe pas par `useBuilder()`.
  const builder = useContext(BuilderContext)
  const ev = (v) =>
    evaluateTemplateValue({ valueToEvaluate: v, globalDataContext, templateContext })

  const evaluatedAttrs = useEvaluatedAttributes(props.attributes)
  const {
    style: userStyle,
    class: authorClass,
    className: authorClassName,
    ...restAttrs
  } = evaluatedAttrs

  const value = ev(props.value)
  const source = resolveSource(value)
  if (!source) return null

  // La boîte réglée par l'éditeur, si elle existe. Tout ce qui suit retombe sur
  // les props YAML quand elle est absente : un bloc jamais redimensionné rend
  // exactement ce qu'il rendait avant.
  const sizing = sizingOf(value)
  const boxes = resolvedBoxes(sizing)

  const declaredOp = sizing?.op ?? props.op ?? 'sc'
  // Le point d'intérêt : il voyage dans l'URL de la dérivée (le serveur
  // découpe autour) ET en `object-position` (c'est ce qui cadre une source
  // non recadrable, et ce qui montre le glissé en édition).
  const focus = focusOf(sizing)
  const declaredW = boxes ? boxes.desktop.w : (Number(ev(props.width)) || 0)
  const declaredH = boxes ? boxes.desktop.h : (Number(ev(props.height)) || 0)
  const token = ev(props.token) || undefined
  const ext = sizing?.ext ?? (props.ext || 'webp')
  const dprs = Array.isArray(props.dprs) ? props.dprs : [1, 2]
  const widths = Array.isArray(props.widths) ? props.widths : undefined
  const cropSource = source.kind === 'remote' ? source.url : source
  const croppable = source.kind === 'dynamic' || source.kind === 'remote'
  // Plafond de l'échelle : la résolution de l'original. Recadrer ne fait que
  // réduire — demander plus large rend une image plus lourde, pas plus nette.
  const sourceWidth = Number(source.width) || 0

  // L'aperçu DÉPLAÇABLE : en édition, sur un cadrage qui découpe et dont on
  // connaît les dimensions de la source. Sans ces dimensions il n'y a rien à
  // calculer — le canevas garde la dérivée découpée, et le geste ne se voit
  // qu'au relâchement.
  const sourceHeight = Number(source.height) || 0

  // UNE OP QUI DÉCOUPE EXIGE SES DEUX CÔTÉS. Le patron du logo — `op: s` avec
  // un côté à 0, la largeur suivant le dessin — devient invalide dès qu'on
  // choisit « Couvrir » ou « Rogner » : `c_580_0` part au serveur, qui le
  // refuse (400), et l'image casse à l'écran. Le côté manquant se déduit donc
  // du rapport de la SOURCE : la découpe garde la forme de l'original, elle ne
  // jette rien, et le cadrage prend effet dès qu'une boîte est réglée.
  //
  // Sans dimensions de source, il n'y a rien à déduire : on retombe sur `s`
  // (mise à l'échelle) plutôt que de composer une URL que le serveur refusera —
  // ne pas recadrer se voit moins qu'une image absente.
  const { op, w, h } = (() => {
    if (declaredOp === 's' || (declaredW && declaredH)) {
      return { op: declaredOp, w: declaredW, h: declaredH }
    }
    const ratio = sourceWidth > 0 && sourceHeight > 0 ? sourceWidth / sourceHeight : 0
    if (ratio && declaredW) return { op: declaredOp, w: declaredW, h: Math.round(declaredW / ratio) }
    if (ratio && declaredH) return { op: declaredOp, w: Math.round(declaredH * ratio), h: declaredH }
    return { op: 's', w: declaredW, h: declaredH }
  })()

  const framable = supportsFocus(op) && croppable && sourceWidth > 0 && sourceHeight > 0
  const livePreview = Boolean(builder?.isEditMode && framable)

  // Sans boîte réglée, c'est la FORME DU FICHIER qui fait la mise en page du
  // bloc (`h-auto`) : servir l'image entière la changerait. La recette
  // d'aperçu rend la boîte de la découpe, et `object-fit` fait le reste.
  const previewFrame = livePreview && !boxes && w > 0 && h > 0

  const className = [
    authorClass,
    authorClassName,
    boxes ? SIZED_CLASS : null,
    previewFrame ? PREVIEW_FRAME_CLASS : null,
    // La position n'est posée que là où elle SERT : une dérivée déjà découpée
    // par le serveur remplit sa boîte, la déplacer n'y change rien.
    (focus || livePreview) ? POS_CLASS : null,
  ].filter(Boolean).join(' ') || undefined
  const style = {
    ...userStyle,
    ...(boxes ? sizingVars(sizing) : null),
    // La variable est posée DÈS QUE la recette l'est, centre compris : c'est
    // elle qui dit au rail que la classe vient de React. Sans ce témoin, le
    // rail retirait la classe en fin de glissé et React ne la remettait pas —
    // sa chaîne de classes n'avait pas changé, donc il ne réécrivait rien, et
    // le cadrage tout juste réglé retombait au centre à l'écran.
    ...((focus || livePreview) ? { '--bimg-pos': focusPosition(focus ?? CENTERED_FOCUS) } : null),
    // `c` (« Rogner ») ne met pas à l'échelle : son aperçu sert l'image à sa
    // taille naturelle, et c'est `object-fit: none` qui reproduit la découpe.
    ...(livePreview && boxes ? { '--bimg-fit': op === 'c' ? 'none' : 'cover' } : null),
    ...(previewFrame
      ? { '--bimg-pratio': `${w} / ${h}`, '--bimg-pfit': op === 'c' ? 'none' : 'cover' }
      : null),
  }

  /**
   * La source de l'aperçu déplaçable : l'image ENTIÈRE, à l'échelle qu'il faut
   * pour couvrir la boîte (`sc`) ou à sa taille naturelle (`c`). Une seule
   * source, sans `srcset` : c'est un rendu d'édition, le navigateur n'a aucun
   * arbitrage à faire.
   */
  const previewSrc = (box) => cropUrl(
    cropSource,
    { op: 's', w: quantize(Math.min(sourceWidth, op === 'c' ? sourceWidth : coverWidth(box, sourceWidth, sourceHeight))), h: 0 },
    { ext, token },
  )

  /** Une source cropée pour une boîte donnée : `{src, srcSet, sizes}`. */
  const forBox = (box) => ({
    src: cropUrl(cropSource, { op, w: box.w, h: box.h, focus }, { ext, token }),
    srcSet: cropSrcSet(
      cropSource,
      { op, w: box.w, h: box.h, focus },
      { ext, token, widths: boxWidths(box, sourceWidth) },
    ),
    sizes: `${box.w}px`,
  })

  // --- quelle(s) source(s) servir ? -----------------------------------------
  let src = ''
  let srcSet = ''
  let sizes = props.sizes || undefined
  let bandSources = null
  // La boîte RÉELLEMENT servie : elle donne les attributs width/height, donc le
  // rapport que le navigateur réserve avant le chargement.
  let servedBox = boxes ? boxes.desktop : null

  if (boxes && croppable) {
    const editedDevice = builder?.isEditMode ? builder.previewDevice : null
    if (editedDevice && boxes[editedDevice]) {
      // (1) En édition : la bande prévisualisée, et elle seule.
      servedBox = boxes[editedDevice]
      if (livePreview) {
        // (1 bis) Cadrage déplaçable : l'image entière, cadrée en CSS.
        src = previewSrc(servedBox)
        srcSet = ''
        sizes = undefined
      } else {
        ;({ src, srcSet, sizes } = forBox(servedBox))
      }
    } else if (ratiosDiffer(boxes)) {
      // (3) Une découpe par bande → `<picture>`.
      bandSources = SIZING_BANDS.map(({ device, media }) => ({ media, ...forBox(boxes[device]) }))
      ;({ src, srcSet } = forBox(boxes.desktop))
      sizes = `${boxes.desktop.w}px`
    } else {
      // (2) Une seule forme, trois largeurs.
      src = cropUrl(cropSource, { op, w, h, focus }, { ext, token })
      srcSet = cropSrcSet(cropSource, { op, w, h, focus }, { ext, token, widths: unionWidths(boxes, sourceWidth) })
      sizes = sizingSizes(boxes)
    }
  } else if (croppable) {
    const spec = { op, w, h, focus }
    const opts = { ext, token }
    // Sans boîte réglée, la découpe est celle du YAML du bloc : le point
    // d'intérêt s'y applique tel quel, et l'aperçu du canevas sert l'image
    // entière comme pour une boîte.
    if (livePreview && w && h) {
      src = previewSrc({ w, h })
      srcSet = ''
    } else {
      src = cropUrl(cropSource, spec, opts)
      srcSet = cropSrcSet(cropSource, spec, { ...opts, dprs, widths })
    }
  } else {
    // Source non recadrable (URL locale nue) : la boîte CSS s'applique quand
    // même — c'est `object-fit` qui la remplit —, mais rien à redimensionner.
    src = source.url
  }

  // Intrinsic size hint for CLS — via the width/height ATTRIBUTES only.
  // We deliberately do NOT set an inline `aspect-ratio`: that would override the
  // author's layout (e.g. `absolute inset-0`, `h-full`, or a container
  // aspect-ratio), forcing the image to derive its height from its width and
  // render far taller than its box. The width/height attributes give the UA an
  // `auto` aspect-ratio that still yields to explicit CSS sizing, so the
  // displayed size matches the box the author defined.
  // (Une image dimensionnée, elle, porte son rapport en variable CSS : c'est la
  // recette `.builder-img-sized` qui l'applique, par bande.)
  // Un seul côté demandé (`op: s` avec l'autre à 0 — le patron d'un logo, dont
  // la largeur suit son dessin) : le côté manquant se DÉDUIT du rapport de la
  // source. Le laisser retomber sur la dimension brute de l'original
  // annoncerait une boîte fausse — largeur demandée d'un côté, hauteur de
  // l'original de l'autre — et le navigateur réserverait une place sans rapport
  // avec l'image (un logo de 200 px de large annoncé haut de 800). Faute de
  // dimensions de source, on n'annonce RIEN plutôt qu'un couple inventé.
  let ratioW = servedBox ? servedBox.w : w
  let ratioH = servedBox ? servedBox.h : h
  if (!servedBox) {
    const sw = Number(source.width) || 0
    const sh = Number(source.height) || 0
    if (ratioW && !ratioH) ratioH = sw && sh ? Math.round(ratioW * sh / sw) : 0
    else if (!ratioW && ratioH) ratioW = sw && sh ? Math.round(ratioH * sw / sh) : 0
    else if (!ratioW && !ratioH) { ratioW = sw; ratioH = sh }
  }
  // Les deux, ou aucun : un seul attribut ne réserve aucune place.
  const hasRatio = ratioW > 0 && ratioH > 0

  // L'`alt` DÉCLARÉ par l'auteur gagne, Y COMPRIS VIDE. Un `alt: ""` posé sur
  // une image décorative (un logo doublé de son intitulé, une photo dont la
  // légende porte déjà le nom) est une DÉCISION : la laisser écraser par le
  // texte alternatif du média la défait, et l'information est annoncée deux
  // fois. Sans déclaration, le média fait foi.
  //
  // La déclaration se lit dans les attributs BRUTS : `evaluateAttributes` jette
  // toute valeur falsy (`m && (f[h] = m)` dans TemplateSystem), donc un
  // `alt: ""` n'arrive jamais jusqu'ici — évalué, il est indiscernable d'un
  // `alt` absent.
  const altDeclared = props.attributes != null
    && Object.prototype.hasOwnProperty.call(props.attributes, 'alt')
  const alt = altDeclared ? (restAttrs.alt ?? '') : (source.alt ?? '')

  const img = (
    <img
      {...restAttrs}
      className={className}
      src={src}
      srcSet={srcSet || undefined}
      sizes={sizes || undefined}
      width={hasRatio ? ratioW : undefined}
      height={hasRatio ? ratioH : undefined}
      alt={alt}
      title={source.title || undefined}
      loading={props.loading || 'lazy'}
      decoding="async"
      style={style}
      // Ce que le canevas a besoin de savoir pour proposer — ou non — une
      // poignée de redimensionnement : cette image vient de LazyImage, et sa
      // source est recadrable à la volée (`crop`) ou pas (`static` : une URL
      // locale nue, que rien ne peut re-générer à une autre taille).
      data-builder-img={croppable ? 'crop' : 'static'}
      // Ce que le rail de cadrage a besoin de savoir en plus : le style de
      // cadrage EFFECTIF (celui du `sizing`, sinon celui du YAML — le rail ne
      // lit pas la déf du bloc), et si l'image affichée est l'image entière
      // (le glissé se voit tout de suite) ou la dérivée déjà découpée (il ne se
      // voit qu'au relâchement).
      data-builder-op={op}
      data-builder-frame={livePreview ? 'live' : 'fixed'}
    />
  )

  return (
    <ActionDependant {...props}>
      {bandSources
        ? (
          <picture>
            {bandSources.map(band => (
              <source key={band.media} media={band.media} srcSet={band.srcSet} sizes={band.sizes} />
            ))}
            {img}
          </picture>
        )
        : img}
    </ActionDependant>
  )
}
